// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMemo } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { AnalysisJobSwimlane } from '@/features/fleet/AnalysisJobSwimlane';
import { AnalysisRunFlow } from '@/features/fleet/AnalysisRunFlow';
import { analysisLogViews, analysisMetricViews, type ExternalView } from '@/features/fleet/analysisLinks';
import { useAnalysisSteps } from '@/features/fleet/useAnalysisSteps';

/**
 * One analysis run, drawn as the stages it went through.
 *
 * Opened in its own window from a course's analysis card, so the run can be read beside the
 * course it belongs to rather than instead of it. The round's jobs come first, as lanes on one
 * clock — which ran side by side, which waited, which the map was decided after — then each
 * provider's stages as the run recorded them (imagery, detect, merge, promote), so it keeps
 * describing the pipeline as the pipeline changes.
 */
export default function AnalysisFlowScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { runId, from } = useLocalSearchParams<{ runId?: string; from?: string }>();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const state = useAnalysisSteps(apiBaseUrl, canView ? (user?.token ?? null) : null, runId ?? null);

  const router = useRouter();
  const close = () => {
    // Back where it was opened from, in order of what preserves most: the history entry if
    // there is one (a native push), otherwise the `from` the opener carried — the admin list
    // with its search and page, which a fresh tab has no history for — and the start screen
    // only when the page was reached by a bare URL.
    if (router.canGoBack()) {
      router.back();
      return;
    }

    const back = returnPath(from);
    router.replace(back ?? '/');
  };

  return (
    <>
      <Stack.Screen options={{ title: t('analysisFlowPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{t('analysisFlowPage.title')}</Text>
          <Pressable onPress={close} accessibilityRole="button" testID="analysis-flow-close" style={styles.button}>
            <Text style={styles.buttonText}>{t('menu.close')}</Text>
          </Pressable>
        </View>

        {!canView ? (
          <Text style={styles.paragraph} testID="analysis-flow-forbidden">
            {t('analysisOverviewPage.forbidden')}
          </Text>
        ) : !runId ? (
          <Text style={styles.paragraph} testID="analysis-flow-missing-run">
            {t('analysisFlowPage.noRun')}
          </Text>
        ) : state.status === 'loading' ? (
          <Text style={styles.paragraph}>{t('batchFleetPage.stepsLoading')}</Text>
        ) : state.status === 'forbidden' ? (
          <Text style={styles.paragraph} testID="analysis-flow-forbidden">
            {t('analysisOverviewPage.forbidden')}
          </Text>
        ) : state.status === 'failed' ? (
          <Text style={styles.paragraph} testID="analysis-flow-failed">
            {t('batchFleetPage.stepsFailed')}
          </Text>
        ) : state.status === 'empty' ? (
          <Text style={styles.paragraph} testID="analysis-flow-empty">
            {t('batchFleetPage.stepsEmpty')}
          </Text>
        ) : (
          <>
            <Text style={styles.runMeta} testID="analysis-flow-run">
              {[
                state.run.courseName ?? state.run.courseId,
                state.run.detector,
                state.run.modelVersion,
                state.run.durationSeconds === null
                  ? null
                  : t('batchFleetPage.stepsSeconds', { seconds: state.run.durationSeconds }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
            {/* The two things needed to find this round anywhere else — its ids and its clock.
                Selectable, because the next step is pasting them into Kibana or a ticket. */}
            <Text style={styles.runIds} selectable testID="analysis-flow-run-ids">
              {[
                t('analysisFlowPage.runId', { id: state.run.runId }),
                state.run.correlationId ? t('analysisFlowPage.correlationId', { id: state.run.correlationId }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
            <Text style={styles.runIds} selectable testID="analysis-flow-run-clock">
              {[
                state.run.requestedAt ? t('analysisFlowPage.requestedAt', { time: formatClock(state.run.requestedAt) }) : null,
                state.run.startedAt ? t('analysisFlowPage.startedAt', { time: formatClock(state.run.startedAt) }) : null,
                state.run.finishedAt ? t('analysisFlowPage.finishedAt', { time: formatClock(state.run.finishedAt) }) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
            {/* The same Kibana and Grafana views the course's analysis card offers, here where
                the ids are: the whole round by correlation id, and the metrics for the run. */}
            <ExternalLinks
              label={t('courseAnalysis.status.externalLogs')}
              views={analysisLogViews({ courseId: state.run.courseId, runId: state.run.runId, correlationId: state.run.correlationId })}
              testIDPrefix="analysis-flow-logs"
            />
            <ExternalLinks
              label={t('courseAnalysis.status.externalMetrics')}
              views={analysisMetricViews({ courseId: state.run.courseId, runId: state.run.runId })}
              testIDPrefix="analysis-flow-metrics"
            />
            {/* What the course's other data sources hold, beside this run: the OSM import and
                Golf i Sverige's scorecard. A run that found little makes more sense next to
                "OSM already mapped this course" or "the scorecard is real". */}
            <View style={styles.sourcesBlock} testID="analysis-flow-sources">
              <Text style={styles.sourcesHeading}>{t('analysisFlowPage.sourcesHeading')}</Text>
              <Text style={styles.sourceLine} testID="analysis-flow-source-osm">
                {state.run.osmFeatures
                  ? t('analysisFlowPage.sourceOsm', {
                      holes: state.run.osmFeatures.holes,
                      greens: state.run.osmFeatures.greens,
                      bunkers: state.run.osmFeatures.bunkers,
                      fairways: state.run.osmFeatures.fairways,
                    })
                  : t('analysisFlowPage.sourceOsmNone')}
              </Text>
              <Text style={styles.sourceLine} testID="analysis-flow-source-scorecard">
                {state.run.scorecardImport
                  ? t('analysisFlowPage.sourceScorecard', {
                      holes: state.run.scorecardImport.holes,
                      tees: state.run.scorecardImport.tees,
                      date: formatClock(state.run.scorecardImport.importedAtUtc),
                    })
                  : t('analysisFlowPage.sourceScorecardNone')}
              </Text>
            </View>
            <AnalysisJobSwimlane
              jobs={state.run.jobs}
              startedAt={state.run.startedAt}
              finishedAt={state.run.finishedAt}
              t={t}
            />
            {state.run.providers.map((provider) => (
              <AnalysisRunFlow key={provider.jobId ?? provider.provider} provider={provider} t={t} />
            ))}
          </>
        )}
      </ScrollView>
    </>
  );
}

/**
 * The `from` parameter as a path this app may navigate to: a relative path of its own, never
 * somewhere else. A page reached with `?from=https://elsewhere` (or `//host`) must not turn its
 * Close button into a link off the site, so anything that is not a single-slash relative path
 * is ignored and Close falls back to the start screen.
 */
const returnPath = (from: string | undefined): string | null => {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return null;
  return from;
};

/** A row of external views (Kibana, Grafana) — web only, where a new tab can open. */
function ExternalLinks({ label, views, testIDPrefix }: { label: string; views: ExternalView[]; testIDPrefix: string }) {
  if (Platform.OS !== 'web' || views.length === 0) return null;
  return (
    <View style={styles.externalRow}>
      <Text style={styles.externalLabel}>{label}</Text>
      {views.map((view) => (
        <Pressable
          key={view.key}
          accessibilityRole="link"
          accessibilityHint={view.hint}
          testID={`${testIDPrefix}-${view.key}`}
          onPress={() => {
            if (typeof window !== 'undefined') window.open(view.url, '_blank', 'noopener');
          }}
        >
          <Text style={styles.externalLink}>{view.label}</Text>
        </Pressable>
      ))}
    </View>
  );
}

/** Local wall clock with seconds — the resolution a log line has. */
const formatClock = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const styles = StyleSheet.create({
  container: { padding: 16, gap: 8 },
  titleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  title: { fontSize: 24, fontWeight: '700', color: appColors.text.primary },
  button: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  buttonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 13 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  runMeta: { color: appColors.text.secondary, fontSize: 13 },
  sourcesBlock: { marginTop: 4, gap: 2 },
  sourcesHeading: { color: appColors.text.primary, fontWeight: '600', fontSize: 15, marginBottom: 4 },
  sourceLine: { color: appColors.text.secondary, fontSize: 13 },
  runIds: { color: appColors.text.muted, fontSize: 12, marginBottom: 4 },
  externalRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 4 },
  externalLabel: { color: appColors.text.muted, fontSize: 12 },
  externalLink: { color: appColors.accent.primary, fontSize: 12, textDecorationLine: 'underline' },
});
